import { useEffect, useState } from 'react';
import type { Account } from '../types';
import {
  ApiError,
  approveAccount,
  confirmBookingAdmin,
  cancelBookingAdmin,
  getAdminAccounts,
  getAdminBookings,
  getAdminStats,
  markSold,
  rejectAccount,
  type AdminBooking,
} from '../lib/api';
import PriceTag from '../components/PriceTag';
import RankBadge from '../components/RankBadge';
import TierCounter from '../components/TierCounter';
import CollectorTierBadge from '../components/CollectorTierBadge';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import { hapticImpact, hapticNotification } from '../lib/telegram';
import { useToastStore } from '../store/toastStore';
import { useCatalogStore } from '../store/catalogStore';

type Tab = 'moderation' | 'bookings';

export default function AdminPage() {
  const [tab, setTab] = useState<Tab>('moderation');
  const [pending, setPending] = useState<Account[]>([]);
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [stats, setStats] = useState<{ activeCount: number; pendingCount: number; bookingsThisWeek: number; soldTotal: number } | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const showToast = useToastStore((s) => s.show);
  const ranks = useCatalogStore((s) => s.meta?.ranks);
  const rankLabel = (rankId: string) => ranks?.find((r) => r.id === rankId)?.name ?? rankId;

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, b, s] = await Promise.all([getAdminAccounts('PENDING'), getAdminBookings(), getAdminStats()]);
      setPending(p);
      setBookings(b);
      setStats(s);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handle = async (fn: () => Promise<unknown>, successMsg: string) => {
    try {
      await fn();
      hapticNotification('success');
      showToast(successMsg);
      refresh();
    } catch (e) {
      hapticNotification('error');
      showToast(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    }
  };

  const doApprove = (id: string) => handle(() => approveAccount(id), 'Одобрено');
  const doReject = () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    handle(() => rejectAccount(rejectTarget, rejectReason.trim()), 'Отклонено');
    setRejectTarget(null);
    setRejectReason('');
  };
  const doSold = (id: string) => handle(() => markSold(id), 'Отмечено как продано');
  const doConfirm = (id: string) => handle(() => confirmBookingAdmin(id), 'Бронь подтверждена');
  const doCancel = (id: string) => handle(() => cancelBookingAdmin(id), 'Бронь отменена');

  return (
    <div className="pb-24">
      {stats && (
        <div className="grid grid-cols-4 gap-2 p-4">
          <StatTile label="Активно" value={stats.activeCount} />
          <StatTile label="На модерации" value={stats.pendingCount} />
          <StatTile label="Брони за неделю" value={stats.bookingsThisWeek} />
          <StatTile label="Продано всего" value={stats.soldTotal} />
        </div>
      )}

      <div className="sticky top-0 z-30 flex border-b border-mlbb-border bg-mlbb-bg/95 backdrop-blur-sm">
        {(
          [
            ['moderation', `Модерация${pending.length ? ` (${pending.length})` : ''}`],
            ['bookings', `Брони${bookings.length ? ` (${bookings.length})` : ''}`],
          ] as [Tab, string][]
        ).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-3 text-sm font-medium ${
              tab === t ? 'border-b-2 border-mlbb-accent-from text-white' : 'text-white/40'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {loading ? (
          <div className="py-10 text-center text-white/40">Загрузка...</div>
        ) : tab === 'moderation' ? (
          pending.length === 0 ? (
            <EmptyState icon="✅" title="Очередь пуста" subtitle="Новых объявлений на модерации нет" />
          ) : (
            <div className="space-y-3">
              {pending.map((a) => (
                <div key={a.id} className="rounded-2xl border border-mlbb-border bg-mlbb-card p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <PriceTag price={a.price} currency={a.currency} />
                    <RankBadge rankId={a.rank} label={rankLabel(a.rank)} />
                  </div>
                  <div className="text-xs text-white/50">
                    Продавец: {a.seller?.username ? `@${a.seller.username}` : a.seller?.firstName} · {a.server}
                  </div>
                  {a.gameId && (
                    <div className="mt-1 text-xs text-white/50">
                      🎮 Game ID: {a.gameId} ({a.serverId})
                    </div>
                  )}
                  <div className="mt-1.5 flex items-center gap-2">
                    <CollectorTierBadge tierId={a.collectorTier} level={a.collectorTierLevel} />
                    <TierCounter legend={a.legendCount} grand={a.grandCount} collector={a.collectorCount} />
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => {
                        hapticImpact('light');
                        doApprove(a.id);
                      }}
                      className="flex-1 rounded-full bg-emerald-600 py-2 text-xs font-medium text-white"
                    >
                      ✅ Одобрить
                    </button>
                    <button
                      onClick={() => {
                        hapticImpact('light');
                        setRejectTarget(a.id);
                      }}
                      className="flex-1 rounded-full bg-red-600 py-2 text-xs font-medium text-white"
                    >
                      ❌ Отклонить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : bookings.length === 0 ? (
          <EmptyState icon="🔖" title="Активных броней нет" />
        ) : (
          <div className="space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="rounded-2xl border border-mlbb-border bg-mlbb-card p-3">
                <div className="mb-2 flex items-center justify-between">
                  <PriceTag price={b.account.price} currency={b.account.currency} />
                  <span className="text-xs text-white/50">{b.status}</span>
                </div>
                <div className="text-xs text-white/60">
                  👤 Покупатель: {b.buyer.username ? `@${b.buyer.username}` : b.buyer.firstName}
                </div>
                <div className="text-xs text-white/60">
                  👤 Продавец:{' '}
                  {b.account.seller?.username ? `@${b.account.seller.username}` : b.account.seller?.firstName}
                </div>
                {b.account.gameId && (
                  <div className="text-xs text-white/60">
                    🎮 Game ID: {b.account.gameId} ({b.account.serverId})
                  </div>
                )}
                {b.note && <div className="mt-1 text-xs text-white/40">Заметка: {b.note}</div>}
                <div className="mt-3 flex gap-2">
                  {b.status === 'PENDING' && (
                    <button
                      onClick={() => doConfirm(b.id)}
                      className="flex-1 rounded-full bg-emerald-600 py-2 text-xs font-medium text-white"
                    >
                      ✅ Подтвердить
                    </button>
                  )}
                  <button
                    onClick={() => doCancel(b.id)}
                    className="flex-1 rounded-full bg-red-600 py-2 text-xs font-medium text-white"
                  >
                    ❌ Отменить
                  </button>
                  <button
                    onClick={() => doSold(b.account.id)}
                    className="flex-1 rounded-full bg-mlbb-accent py-2 text-xs font-medium text-white"
                  >
                    💰 Продано
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!rejectTarget}
        title="Причина отклонения"
        confirmLabel="Отклонить"
        danger
        onConfirm={doReject}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason('');
        }}
      >
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Например: скриншоты не соответствуют описанию"
          rows={3}
          className="input resize-none"
        />
      </ConfirmModal>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-mlbb-border bg-mlbb-card p-2.5 text-center">
      <div className="text-lg font-bold text-white">{value}</div>
      <div className="text-[10px] text-white/50">{label}</div>
    </div>
  );
}
